import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';
import { queryKeys } from '../utils/queryKeys';
import { useQueryError } from './useQueryError';

const fetchCardData = async (cardId) => {
  const { data, error } = await supabase
    .from("cards")
    .select("*, card_color, vid_brd_color")
    .eq("id", cardId)
    .single();

  if (error) throw error;
  
  return {
    ...data,
    cardColor: data.card_color || "#3A3A39",
    videoBorderColor: data.vid_brd_color || null
  };
};

const fetchCursor = async () => {
  const { data, error } = await supabase
    .from("assets")
    .select("link")
    .eq("id", "271902ef-abd4-409c-af82-9c7432d4ed1e")
    .single();

  if (error) throw error;
  return data.link;
};

export function useCard(cardId) {
  const queryClient = useQueryClient();
  const { handleError } = useQueryError();

  // Handler for query errors
  const handleQueryError = (error) => {
    handleError({
      title: 'Error loading card data',
      message: error.message,
      severity: 'error'
    });
  };

  // Query principal para los datos de la card
  const {
    data: cardData,
    isLoading: isCardLoading,
    error: cardError
  } = useQuery({
    queryKey: queryKeys.cards.byId(cardId),
    queryFn: () => fetchCardData(cardId),
    enabled: Boolean(cardId),
    staleTime: 1000 * 60 * 5, // 5 minutos
    cacheTime: 1000 * 60 * 10, // 10 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    onError: handleQueryError
  });

  // Query para el cursor SVG
  const {
    data: cursorSvg,
    isLoading: isCursorLoading
  } = useQuery({
    queryKey: queryKeys.assets.byId("271902ef-abd4-409c-af82-9c7432d4ed1e"),
    queryFn: fetchCursor,
    staleTime: 1000 * 60 * 60 * 24 // 24 horas - el cursor SVG raramente cambia
  });

  // Función para prefetch de card y recursos relacionados
  const prefetchCard = async (nextCardId) => {
    if (!nextCardId) return;

    try {
      // Prefetch datos básicos de la card
      await queryClient.prefetchQuery({
        queryKey: queryKeys.cards.byId(nextCardId),
        queryFn: () => fetchCardData(nextCardId),
        staleTime: 1000 * 60 * 5
      });

      // Prefetch de video y assets relacionados
      const cardData = await fetchCardData(nextCardId);
      if (cardData?.videoclosed) {
        const videoUrl = cardData.videoclosed;
        // Prefetch del video usando un objeto Image
        new Image().src = videoUrl;
      }

      // Prefetch datos de la compañía si están disponibles
      if (cardData?.companyoverviewid) {
        await queryClient.prefetchQuery({
          queryKey: queryKeys.company.byId(cardData.companyoverviewid),
          queryFn: async () => {
            const { data, error } = await supabase
              .from("company_overview")
              .select("*")
              .eq("id", cardData.companyoverviewid)
              .single();

            if (error) throw error;
            return data;
          },
          staleTime: 1000 * 60 * 5
        });
      }
    } catch (error) {
      // Manejo silencioso de errores de prefetch
      console.warn('Prefetch warning:', error);
    }
  };

  const isLoading = isCardLoading || isCursorLoading;
  const error = cardError;

  return {
    cardData,
    cursorSvg,
    isLoading,
    error,
    prefetchCard // Exportamos la función de prefetch
  };
}